import { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import { TablePagination, TableRow, TableCell, Button } from "@mui/material";
import { ArrowBackIosNewRounded } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "@uidotdev/usehooks";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";

import isDesktop from "../isDesktop";
import PlanSelectionMobile from "./PlanSelectionMobile";
import styles from "./planSelection.module.css";
import {
  findBackendTabByName,
  getStoredBackendTabs,
  getStoredTabIds,
  isAdminUser,
} from "../utils/access";
import roleService from "../roles/roleService";

const PlanSelection = () => {
  const isDesktopDevice = isDesktop();
  const navigate = useNavigate();

  // --- State ---
  const [planSelections, setPlanSelections] = useState([]);
  const [filteredPlanSelections, setFilteredPlanSelections] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // eslint-disable-next-line no-unused-vars
  const [token, setToken] = useLocalStorage("token", null);

  const userTabIds = getStoredTabIds();
  const [backendTabs, setBackendTabs] = useState(getStoredBackendTabs);
  const [tabsLoading, setTabsLoading] = useState(() => getStoredBackendTabs().length === 0);
  const planSelectedTab = findBackendTabByName(
    backendTabs,
    "Plan Selected"
  );
  const isAuthorized =
    isAdminUser() ||
    tabsLoading ||
    (planSelectedTab ? userTabIds.includes(planSelectedTab.tab_id) : false);

  // --- Pagination helpers ---
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const displayedPlanSelections = filteredPlanSelections.slice(startIndex, endIndex);
  const emptyRows =
    rowsPerPage -
    Math.min(rowsPerPage, filteredPlanSelections.length - page * rowsPerPage);

  // --- Date formatting ---
  function formatDate(dateTimeString) {
    const date = new Date(dateTimeString);
    const formattedDate = `${date.getDate().toString().padStart(2, "0")}-${(date.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${date.getFullYear()}`;
    const formattedTime = `${date.getHours().toString().padStart(2, "0")}:${date
      .getMinutes()
      .toString()
      .padStart(2, "0")}:${date.getSeconds().toString().padStart(2, "0")}`;
    return `${formattedDate} ${formattedTime}`;
  }

  // --- Fetch: plan selections only (no user enrichment) ---
  const fetchPlanSelections = async () => {
    try {
      const baseUrl = `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_KEY}`;
      const url = `${baseUrl}/plan-selections?limit=200&offset=0&sort=selected_at.desc`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const rows = res?.data?.data || [];

      const normalized = rows.map((r) => ({
        select_id: r.id ?? r.select_id ?? r.selectId ?? "-",
        user_id: r.user_id ?? r.uid ?? r.userId ?? "-",
        class: r.class ?? "-",
        goal: r.goal ?? "-",
        plan_name: r.plan_name ?? "-",
        selected_at: r.selected_at,
      }));

      setPlanSelections(normalized);
      setFilteredPlanSelections(normalized);
    } catch (err) {
      console.error("Error fetching plan selections:", err);
      toast.error("Failed to load Plan Selections. Please try again.");
    }
  };

  // --- Search (since name/username not available, filter by user_id or plan_name) ---
  const filterSelections = (query) => {
    if (!query) {
      setFilteredPlanSelections(planSelections);
    } else {
      const q = query.toLowerCase();
      const filtered = planSelections.filter((row) => {
        const uid = String(row.user_id ?? "").toLowerCase();
        const plan = (row.plan_name ?? "").toLowerCase();
        return uid.includes(q) || plan.includes(q);
      });
      setFilteredPlanSelections(filtered);
    }
    setPage(0);
  };

  useEffect(() => {
    if (isDesktopDevice) fetchPlanSelections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    let isMounted = true;

    const fetchTabs = async () => {
      if (!token || isAdminUser()) return;

      setTabsLoading(true);
      try {
        const activeTabs = await roleService.getTabs(token);
        if (!isMounted) return;

        setBackendTabs(activeTabs);
        localStorage.setItem("backend_tabs", JSON.stringify(activeTabs));
      } catch (error) {
        console.error("Error fetching tabs:", error);
      } finally {
        if (isMounted) setTabsLoading(false);
      }
    };

    fetchTabs();

    return () => {
      isMounted = false;
    };
  }, [token]);

  useEffect(() => {
    isDesktopDevice && filterSelections(searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, planSelections]);

  // --- Pagination handlers ---
  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  if (!isAuthorized) {
    return (
      <div className="container-fluid mt-5">
        <h2 style={{ marginTop: "5%" }}>You are unauthorized to access this page</h2>
      </div>
    );
  }

  return (
    <>
      {isDesktopDevice ? (
        <div className="container-fluid" style={{ marginTop: "4rem" }}>
          <div className="align-center-all full-height">
            <Card elevation={3} className="col-md-12 p-3">
              <CardContent>
                {/* Header */}
                <div className={styles.header}>
                  <div
                    style={{
                      display: "flex",
                      gap: "1vw",
                      alignItems: "center",
                    }}
                  >
                    <ArrowBackIosNewRounded
                      onClick={() => navigate("/dashboard")}
                      style={{ cursor: "pointer" }}
                    />
                    <h2 style={{ marginRight: "1rem", marginBottom: "0px" }} className="page-header">
                      Plan Selected
                    </h2>
                  </div>

                  <div className={styles.headerRight}>
                    <TablePagination
                      rowsPerPageOptions={[10, 20, 30]}
                      component="div"
                      count={filteredPlanSelections.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                    <TextField
                      type="text"
                      placeholder="Search by user id or plan"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": { borderColor: "transparent" },
                          "&:hover fieldset": { borderColor: "transparent" },
                          "&.Mui-focused fieldset": { borderColor: "transparent", boxShadow: "none" },
                        },
                      }}
                      InputProps={{
                        style: {
                          height: "2.564vw",
                          boxShadow:
                            "0px 1.74px 3.48px 0px #0000001A inset, 0px -1.74px 3.8px 0px #C8C7C740 inset, 0px 1.74px 3.48px 0px #0000001A",
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton>
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                </div>

                <ToastContainer />

                {/* Table */}
                <table className={styles.mainTable}>
                  <thead className={styles.tableHead}>
                    <tr>
                      <th style={{ textAlign: "center", width: "6.538vw" }}>S. No.</th>
                      <th style={{ width: "12.5vw", textAlign: "left" }}>User ID</th>
                      <th style={{ width: "10.513vw", textAlign: "left" }}>Class</th>
                      <th style={{ width: "12.513vw", textAlign: "left" }}>Goal</th>
                      <th style={{ width: "14.513vw", textAlign: "left" }}>Plan Name</th>
                      <th style={{ textAlign: "center", width: "15.641vw" }}>Selected Date & Time</th>
                      <th style={{ textAlign: "left", paddingLeft: "5.128vw" }}>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {displayedPlanSelections.map((row) => (
                      <tr key={`${row.select_id}-${row.user_id}`} className={styles.tableBody}>
                        <td style={{ fontSize: "1.026vw", textAlign: "center" }}>
                          {row.select_id}
                        </td>
                        <td style={{ textAlign: "left", fontSize: "1.026vw" }}>
                          {row.user_id}
                        </td>
                        <td style={{ textAlign: "left", fontSize: "1.026vw" }}>{row.class}</td>
                        <td style={{ textAlign: "left", fontSize: "1.026vw" }}>{row.goal}</td>
                        <td style={{ textAlign: "left", fontSize: "1.026vw" }}>{row.plan_name}</td>
                        <td style={{ width: "10vw", fontSize: "1.026vw", textAlign: "center" }}>
                          {formatDate(row.selected_at)}
                        </td>
                        <td style={{ paddingLeft: "5.128vw" }}>
                          <div className={styles.buttonContainer}>
                            <Button
                              variant="contained"
                              disabled
                              style={{
                                height: "2.051vw",
                                color: "black",
                                backgroundColor: "#E6E6E6",
                                textTransform: "capitalize",
                                padding: "0.449vw 0.897vw",
                              }}
                              sx={{ fontSize: "0.897vw" }}
                            >
                              —
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}

                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={7} />
                      </TableRow>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        <PlanSelectionMobile />
      )}
    </>
  );
};

export default PlanSelection;
